import { Crosshair, Thermometer, Power, Gauge, Bell, GraduationCap } from "lucide-react";

const PRODUCT_IMG =
    "https://static.prod-images.emergentagent.com/jobs/0a053d95-73ab-41d8-8498-cb7090195340/images/d56af880a2bb67e71305a7932f41d7657cf2cda3fb00944c5ac8bfd68b3e0747.png";

const FEATURES = [
    {
        id: "01",
        icon: Crosshair,
        title: "Fixed Nozzles",
        body: "Securely positioned. They will never shift during cleaning — so your aim, and your protection, never changes.",
    },
    {
        id: "02",
        icon: Thermometer,
        title: "Two-Stage Heat Sensor",
        body: "An audible elevated-heat warning sounds before activation, giving staff time to lower heat and avoid unwanted shutdowns.",
    },
    {
        id: "03",
        icon: Power,
        title: "Auto Gas / Electric Shutoff",
        body: "On activation, Kitchensafe automatically disconnects the gas or electric source — eliminating the fuel feeding the fire.",
    },
    {
        id: "04",
        icon: Gauge,
        title: "Cylinder Pressure Monitor",
        body: "A low-suppressant cylinder pressure warning keeps you ahead of maintenance. No surprises at inspection.",
    },
    {
        id: "05",
        icon: Bell,
        title: "Central Alarm Terminal",
        body: "Doubles as a fire alarm system. Self-contained terminals — no extra modules, no external boxes. Mountable up to 15 ft from the hood, ideal for island kitchens.",
    },
    {
        id: "06",
        icon: GraduationCap,
        title: "Free Training & Same-Day Support",
        body: "Free video training and certification. We request same-day installation support — it saves the installer time and prevents accidental activation. Every system ships fire-marshal ready.",
    },
];

function FeaturesHeader() {
    return (
        <div className="grid lg:grid-cols-12 gap-8 mb-12 md:mb-16">
            <div className="lg:col-span-5">
                <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935]">
                        / 01 — The System
                    </span>
                </div>
                <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                    Engineered for{" "}
                    <span className="text-[#E53935] italic font-medium">prevention</span>. Not
                    reaction.
                </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                <p className="text-base md:text-lg text-[#a1a1aa] leading-relaxed">
                    Six engineered components working as one self-contained unit. Every Kitchensafe
                    ships complete, ready for fire-marshal inspection — no shopping list, no
                    surprises.
                </p>
            </div>
        </div>
    );
}

function HeroCell() {
    return (
        <div className="md:col-span-2 lg:col-span-2 lg:row-span-2 relative bg-[#0f0f12] overflow-hidden group min-h-[400px]">
            <img
                src={PRODUCT_IMG}
                alt="Kitchensafe installed under a stainless-steel range hood"
                className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/30 to-transparent" />
            <div className="absolute top-6 left-6 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#22c55e] rounded-full pulse-dot" />
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#a1a1aa]">
                    Stainless Build · No Shortcuts
                </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935] mb-3">
                    Built Stainless
                </div>
                <h3 className="font-display font-bold text-3xl md:text-4xl text-white leading-tight">
                    Stainless steel.
                    <br />
                    Built to last.
                </h3>
                <p className="mt-4 text-sm md:text-base text-[#a1a1aa] max-w-md leading-relaxed">
                    Nozzles mount to a stainless-steel manifold beneath the hood — our most common
                    install. When the layout calls for it, nozzles can also be inserted directly
                    into the hood. The 1-liter UL300a cylinder, manifold, fittings, and supply
                    lines are all stainless steel. No rust. No fatigue. Built for the long haul.
                </p>
            </div>
        </div>
    );
}

function FeatureCell({ feature }) {
    const Icon = feature.icon;
    return (
        <div
            data-testid={`feature-${feature.id}`}
            className="group relative bg-[#0a0a0b] hover:bg-[#141416] transition-colors p-8 md:p-10"
        >
            <div className="flex items-center justify-between mb-6">
                <div className="w-10 h-10 flex items-center justify-center border border-white/10 group-hover:border-[#E53935]/50 group-hover:bg-[#E53935]/5 transition-all rounded-sm">
                    <Icon className="w-4 h-4 text-white" strokeWidth={1.75} />
                </div>
                <span className="font-mono-tech text-[11px] text-[#71717a] tracking-[0.22em]">
                    {feature.id}
                </span>
            </div>
            <h3 className="font-display font-bold text-xl text-white mb-2 tracking-tight">
                {feature.title}
            </h3>
            <p className="text-sm md:text-base text-[#a1a1aa] leading-relaxed">{feature.body}</p>
        </div>
    );
}

export default function Features() {
    return (
        <section
            id="features"
            data-testid="features-section"
            className="relative py-20 md:py-24 border-t border-white/10"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <FeaturesHeader />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/10">
                    <HeroCell />
                    {FEATURES.map((f) => (
                        <FeatureCell key={f.id} feature={f} />
                    ))}
                </div>
            </div>
        </section>
    );
}
